import { customWorldCandidates, flatPresets, WorldGenerationSchema, type WorldGeneration } from '@mcpanel/core';
import { FieldError } from './FieldError';

const biomes = [
  'plains',
  'forest',
  'desert',
  'taiga',
  'snowy_plains',
  'jungle',
  'savanna',
  'badlands',
  'deep_ocean',
  'the_void',
];
/** スーパーフラットの「何もない」プリセット（the_void）と同じ層・バイオームなら、画面ではボイドとして扱う */
export function isVoid(value: WorldGeneration): boolean {
  if (value.worldType !== 'flat') return false;
  const preset = flatPresets.the_void!.settings;
  return (
    value.flat.biome === preset.biome &&
    value.flat.layers.length === 1 &&
    value.flat.layers[0]!.block === preset.layers[0]!.block
  );
}
type TypeChoice = WorldGeneration['worldType'] | 'void';

export function WorldGenerationFields({
  value,
  onChange,
  mods,
  candidates = [],
}: {
  value: WorldGeneration;
  onChange: (value: WorldGeneration) => void;
  mods: Array<{ project: string }>;
  candidates?: Array<{ id: string; label: string }>;
}) {
  const parsed = WorldGenerationSchema.safeParse(value);
  const messages = (path: string) =>
    parsed.success
      ? []
      : parsed.error.issues.filter((issue) => issue.path.join('.') === path).map((issue) => issue.message);
  const common = { seed: value.seed, generateStructures: value.generateStructures };
  const voidSelected = isVoid(value);
  function changeType(worldType: TypeChoice) {
    // ボイドは、スーパーフラットの「何もない」プリセットで作る。構造物も作らない。
    if (worldType === 'void')
      onChange({
        seed: value.seed,
        generateStructures: false,
        worldType: 'flat',
        flat: structuredClone(flatPresets.the_void!.settings),
      });
    else if (worldType === 'flat')
      onChange({ ...common, worldType, flat: structuredClone(flatPresets.classic_flat!.settings) });
    else if (worldType === 'single_biome') onChange({ ...common, worldType, biome: 'minecraft:plains' });
    else if (worldType === 'custom') onChange({ ...common, worldType, customId: '' });
    else onChange({ ...common, worldType });
  }
  return (
    <section className="card card-body stack">
      <h2>ワールドの生成</h2>
      <p>サーバーの新規作成・ワールドの再生成時に使います。今のワールドの地形は変わりません。</p>
      <div className="field">
        <label htmlFor="world-type">生成方法</label>
        <select
          data-search-id="world-type"
          id="world-type"
          className="input"
          value={voidSelected ? 'void' : value.worldType}
          onChange={(e) => changeType(e.target.value as TypeChoice)}
        >
          {(
            [
              ['normal', '通常'],
              ['flat', 'スーパーフラット'],
              ['void', 'ボイド（何もない）'],
              ['large_biomes', '大きなバイオーム'],
              ['amplified', 'アンプリファイド'],
              ['single_biome', 'シングルバイオーム'],
              ['custom', 'Mod・データパック'],
            ] as const
          ).map(([id, label]) => (
            <option value={id} key={id}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="world-seed">シード値（任意）</label>
        <input
          data-search-id="world-seed"
          id="world-seed"
          className="input"
          value={value.seed ?? ''}
          maxLength={200}
          onChange={(e) => onChange({ ...value, seed: e.target.value })}
          aria-describedby="world-seed-error"
        />
        <FieldError id="world-seed-error" messages={messages('seed')} />
      </div>
      <label className="check" data-search-id="world-structures">
        <input
          type="checkbox"
          checked={value.generateStructures}
          onChange={(e) => onChange({ ...value, generateStructures: e.target.checked })}
        />
        構造物を生成
      </label>
      {value.worldType === 'custom' && (
        <div className="field">
          <label htmlFor="world-custom">生成方法の ID（namespace:id）</label>
          <input
            data-search-id="world-custom"
            id="world-custom"
            className="input"
            list="world-mod-types"
            value={value.customId}
            onChange={(e) => onChange({ ...value, customId: e.target.value })}
            aria-describedby="world-custom-error"
          />
          <FieldError id="world-custom-error" messages={messages('customId')} />
          <datalist id="world-mod-types">
            {[...new Map([...candidates, ...customWorldCandidates(mods)].map((c) => [c.id, c])).values()].map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </datalist>
          <p>必要な Mod・データパックを初回起動前に導入してください。候補がない場合は ID を手入力できます。</p>
        </div>
      )}
      {voidSelected && value.worldType === 'flat' && (
        <div className="stack">
          <label className="check" data-search-id="void-platform">
            <input
              type="checkbox"
              checked={value.flat.features}
              onChange={(e) => onChange({ ...value, flat: { ...value.flat, features: e.target.checked } })}
            />
            スポーン地点に小さな石の足場を置く
          </label>
          {!value.flat.features && (
            <p className="alert warn" role="status">
              足場が無いと、最初にワールドに入った人は下へ落ちます。建築用に使う場合は、先にクリエイティブモードにしてください。
            </p>
          )}
        </div>
      )}
      {(value.worldType === 'single_biome' || (value.worldType === 'flat' && !voidSelected)) && (
        <div className="field">
          <label htmlFor="world-biome">バイオーム ID</label>
          <input
            data-search-id="world-biome"
            id="world-biome"
            className="input"
            list="world-biomes"
            aria-describedby="world-biome-error"
            value={value.worldType === 'flat' ? value.flat.biome : value.biome}
            onChange={(e) =>
              onChange(
                value.worldType === 'flat'
                  ? { ...value, flat: { ...value.flat, biome: e.target.value } }
                  : { ...value, biome: e.target.value },
              )
            }
          />
          <FieldError id="world-biome-error" messages={messages(value.worldType === 'flat' ? 'flat.biome' : 'biome')} />
          <datalist id="world-biomes">
            {biomes.map((id) => (
              <option key={id} value={`minecraft:${id}`} />
            ))}
          </datalist>
        </div>
      )}
      {value.worldType === 'single_biome' && (
        <p>対応バージョン: Minecraft 1.21.4〜1.21.10。選んだバイオームをデータパックで指定します。</p>
      )}
      {value.worldType === 'flat' && !voidSelected && (
        <>
          <div className="field">
            <label htmlFor="flat-preset">フラットのプリセット</label>
            <select
              data-search-id="flat-preset"
              id="flat-preset"
              className="input"
              value=""
              onChange={(e) => {
                const preset = flatPresets[e.target.value];
                if (preset) onChange({ ...value, flat: structuredClone(preset.settings) });
              }}
            >
              <option value="">プリセットから層を設定</option>
              {Object.entries(flatPresets).map(([id, p]) => (
                <option key={id} value={id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <p>層は下から上の順です。合計の厚さは 384 以下にしてください。</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>ブロック ID</th>
                  <th>厚さ（ブロック）</th>
                  <th>順序・削除</th>
                </tr>
              </thead>
              <tbody>
                {value.flat.layers.map((layer, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        className="input"
                        aria-label={`層 ${index + 1} のブロック`}
                        aria-describedby={`flat-block-${index}-error`}
                        value={layer.block}
                        onChange={(e) =>
                          onChange({
                            ...value,
                            flat: {
                              ...value.flat,
                              layers: value.flat.layers.map((l, i) =>
                                i === index ? { ...l, block: e.target.value } : l,
                              ),
                            },
                          })
                        }
                      />
                      <FieldError id={`flat-block-${index}-error`} messages={messages(`flat.layers.${index}.block`)} />
                    </td>
                    <td>
                      <input
                        className="input"
                        type="number"
                        min={1}
                        max={384}
                        aria-label={`層 ${index + 1} の厚さ`}
                        aria-describedby={`flat-height-${index}-error`}
                        value={layer.height}
                        onChange={(e) =>
                          onChange({
                            ...value,
                            flat: {
                              ...value.flat,
                              layers: value.flat.layers.map((l, i) =>
                                i === index ? { ...l, height: Number(e.target.value) } : l,
                              ),
                            },
                          })
                        }
                      />
                      <FieldError
                        id={`flat-height-${index}-error`}
                        messages={messages(`flat.layers.${index}.height`)}
                      />
                    </td>
                    <td>
                      {([-1, 1] as const).map((delta) => (
                        <button
                          key={delta}
                          type="button"
                          className="btn small"
                          aria-label={`層 ${index + 1} を${delta === -1 ? '下' : '上'}へ`}
                          disabled={index + delta < 0 || index + delta >= value.flat.layers.length}
                          onClick={() => {
                            const layers = [...value.flat.layers];
                            [layers[index], layers[index + delta]] = [layers[index + delta]!, layers[index]!];
                            onChange({ ...value, flat: { ...value.flat, layers } });
                          }}
                        >
                          {delta === -1 ? '↓' : '↑'}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="btn small"
                        aria-label={`層 ${index + 1} を削除`}
                        disabled={value.flat.layers.length === 1}
                        onClick={() =>
                          onChange({
                            ...value,
                            flat: { ...value.flat, layers: value.flat.layers.filter((_, i) => i !== index) },
                          })
                        }
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <FieldError id="flat-layers-error" messages={[...messages('flat.layers'), ...messages('flat')]} />
          <button
            type="button"
            className="btn"
            onClick={() =>
              onChange({
                ...value,
                flat: { ...value.flat, layers: [...value.flat.layers, { block: 'minecraft:stone', height: 1 }] },
              })
            }
          >
            層を追加
          </button>
          {(
            [
              ['structures', 'フラット内の構造物'],
              ['features', '地形の装飾'],
              ['lakes', '湖'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="check" data-search-id={`flat-${key}`}>
              <input
                type="checkbox"
                checked={value.flat[key]}
                onChange={(e) => onChange({ ...value, flat: { ...value.flat, [key]: e.target.checked } })}
              />
              {label}
            </label>
          ))}
        </>
      )}
    </section>
  );
}
